import {
	useCallback,
	useEffect,
	useMemo,
	useReducer,
	useState,
	type FC,
	type PropsWithChildren,
	type Reducer,
} from "react";
import { ScrollSpyContext } from "./context";

// The different types of actions that our reducer can perform
enum ActionTypes {
	Register = "REGISTER",
	UnRegister = "UNREGISTER",
	SetInView = "SET_INVIEW",
}

type Spy = {
	id: string;
	inView: boolean;
};

type ScrollSpyState = {
	items: Spy[];
};

// Discriminating Union types for our actions
type ActionRegister = {
	type: ActionTypes.Register;
	payload: Spy;
};

type ActionUnRegister = {
	type: ActionTypes.UnRegister;
	payload: string;
};

type ActionSetInView = {
	type: ActionTypes.SetInView;
	payload: Spy;
};

type ScrollSpyAction = ActionRegister | ActionUnRegister | ActionSetInView;

const intialState: ScrollSpyState = {
	items: [],
};

const isAtBottomOfPage = (): boolean =>
	window.innerHeight + window.scrollY >=
	window.document.body.offsetHeight - window.innerHeight / 4;

const reducer: Reducer<ScrollSpyState, ScrollSpyAction> = (state, action) => {
	switch (action.type) {
		case ActionTypes.Register: {
			return { ...state, items: [...state.items, action.payload] };
		}
		case ActionTypes.UnRegister: {
			return {
				...state,
				items: state.items.filter((item) => item.id !== action.payload),
			};
		}
		case ActionTypes.SetInView: {
			return {
				...state,
				items: state.items.map((item) =>
					item.id === action.payload.id ? action.payload : item,
				),
			};
		}
		default: {
			return state;
		}
	}
};

type ScrollSpyProps = PropsWithChildren<{
	onChange?: (id?: string) => void;
}>;

const ScrollSpy: FC<ScrollSpyProps> = ({ children, onChange }) => {
	const [state, dispatch] = useReducer(reducer, intialState);
	const [atBottomOfPage, setAtBottomOfPage] = useState<boolean>(false);

	const register = useCallback((id: string) => {
		dispatch({
			type: ActionTypes.Register,
			payload: { id, inView: false },
		});
	}, []);

	const unRegister = useCallback((id: string) => {
		dispatch({
			type: ActionTypes.UnRegister,
			payload: id,
		});
	}, []);

	const setInView = useCallback((id: string, inView: boolean) => {
		dispatch({
			type: ActionTypes.SetInView,
			payload: {
				id,
				inView,
			},
		});
	}, []);

	const active = useMemo(
		() =>
			atBottomOfPage
				? state.items.at(-1)
				: state.items.find((item) => item.inView),
		[state, atBottomOfPage],
	);

	useEffect(() => {
		const onScroll = () => {
			setAtBottomOfPage(isAtBottomOfPage());
		};

		window.addEventListener("scroll", onScroll);

		return () => {
			window.removeEventListener("scroll", onScroll);
		};
	}, []);

	useEffect(() => {
		if (onChange) {
			onChange(active ? active.id : undefined);
		}
	}, [active, onChange]);

	const scrollSpyProviderValue = useMemo(
		() => ({
			register,
			unRegister,
			setInView,
		}),
		[register, unRegister, setInView],
	);

	return (
		<ScrollSpyContext.Provider value={scrollSpyProviderValue}>
			{children}
		</ScrollSpyContext.Provider>
	);
};

export default ScrollSpy;
